import type { Worksheet } from "exceljs";
import type { Department } from "../../../contexts/AuthProvider";
import { COLUMN_TYPE_TO_HEADER } from "../constants";
import type { CustomColumnNamesConfig, DailyPrintoutGrouping, PrintoutColumn } from "../types";
import { compositeGroupingKeyToDisplayName, resolveColumnHeaderText } from "../util/groupingDisplay";
import { COLUMN_HEADER_FILL, SECTION_HEADER_FILL, SUBHEADER_FILL, THIN_BORDER } from "./cellStyle";
import { mergeAcrossRow } from "./rowMerge";

interface RenderStandardSectionHeaderArgs {
	worksheet: Worksheet;
	/** Pre-resolved title to display (raw — not passed through groupingKey display formatting). */
	title: string;
	currentRow: number;
	expandedColumns: PrintoutColumn[];
	printNotes: boolean;
	jobManagementEnabled: boolean;
	customColumnNames: CustomColumnNamesConfig;
	/** Leave out the column-header row; the title row stays and the data starts right under it. */
	hideColumnNames: boolean;
}

/**
 * Title row + column-header row, no stats in between. Convenience for the PTO section header.
 *
 * Takes a raw `title` (not a groupingKey) because PTO's title is a fixed string ("Time Off / PTO")
 * — not derived from any grouping kind. Routing it through `groupingKeyToDisplayName` with
 * `grouping=[]` would always resolve to "All Staff" because the NONE branch hardcodes that label.
 */
export const renderStandardSectionHeader = (args: RenderStandardSectionHeaderArgs): number => {
	const { worksheet, title, currentRow, expandedColumns, printNotes, jobManagementEnabled, customColumnNames, hideColumnNames } = args;
	const hasNotesInColumns = expandedColumns.some((col) => col.type === "notes");
	const totalColumns = expandedColumns.length + (printNotes && !hasNotesInColumns ? 1 : 0);
	const row = emitRawTitle({ worksheet, title, currentRow, totalColumns });
	if (hideColumnNames) return row;
	return emitColumnHeaders({ worksheet, currentRow: row, expandedColumns, printNotes, jobManagementEnabled, customColumnNames });
};

interface EmitRawTitleArgs {
	worksheet: Worksheet;
	title: string;
	currentRow: number;
	totalColumns: number;
}

/**
 * Emit a section title row with a literal title string (no display-name resolution).
 */
const emitRawTitle = ({ worksheet, title, currentRow, totalColumns }: EmitRawTitleArgs): number => {
	const titleCell = worksheet.getCell(`A${currentRow}`);
	titleCell.value = title;
	titleCell.font = { bold: true, size: 14, color: { argb: "FF000000" } };
	titleCell.fill = SECTION_HEADER_FILL;
	titleCell.alignment = { horizontal: "left" };
	mergeAcrossRow(worksheet, currentRow, totalColumns);
	return currentRow + 1;
};

interface EmitSectionTitleArgs {
	worksheet: Worksheet;
	groupingKey: string;
	currentRow: number;
	grouping: DailyPrintoutGrouping;
	totalColumns: number;
	department: Department;
	jobManagementEnabled: boolean;
	/** When set, a location section segment gets ` - <census>` appended (show-location-census). */
	censusByLocationName: Map<string, number>;
}

/**
 * Emit the section title row only (used by both standard and pivot section flows).
 *
 * Returns the next row. In the standard flow, role-group stats may be emitted between this row
 * and the column-header row by the caller.
 */
export const emitSectionTitle = ({
	worksheet,
	groupingKey,
	currentRow,
	grouping,
	totalColumns,
	department,
	jobManagementEnabled,
	censusByLocationName,
}: EmitSectionTitleArgs): number => {
	const titleCell = worksheet.getCell(`A${currentRow}`);
	titleCell.value = compositeGroupingKeyToDisplayName(groupingKey, grouping, jobManagementEnabled, department, censusByLocationName);
	titleCell.font = { bold: true, size: 14, color: { argb: "FF000000" } };
	titleCell.fill = SECTION_HEADER_FILL;
	titleCell.alignment = { horizontal: "left" };
	mergeAcrossRow(worksheet, currentRow, totalColumns);
	return currentRow + 1;
};

interface EmitColumnHeadersArgs {
	worksheet: Worksheet;
	currentRow: number;
	expandedColumns: PrintoutColumn[];
	printNotes: boolean;
	jobManagementEnabled: boolean;
	customColumnNames: CustomColumnNamesConfig;
}

/**
 * Emit the column-header row for the standard (non-pivot) layout.
 *
 * Returns the next row.
 */
export const emitColumnHeaders = ({
	worksheet,
	currentRow,
	expandedColumns,
	printNotes,
	jobManagementEnabled,
	customColumnNames,
}: EmitColumnHeadersArgs): number => {
	const hasNotesInColumns = expandedColumns.some((col) => col.type === "notes");
	const headers = expandedColumns.map((col) => resolveColumnHeaderText(col.type, jobManagementEnabled, customColumnNames));
	if (printNotes && !hasNotesInColumns) headers.push(COLUMN_TYPE_TO_HEADER.notes);
	headers.forEach((header, index) => {
		const cell = worksheet.getCell(currentRow, index + 1);
		cell.value = header;
		cell.font = { bold: true };
		cell.fill = COLUMN_HEADER_FILL;
		cell.border = THIN_BORDER;
	});
	return currentRow + 1;
};

interface RenderSubheaderArgs {
	worksheet: Worksheet;
	currentRow: number;
	title: string;
	columnCount: number;
}

/**
 * Sub-section title row — used inside the PTO section for the per-group label.
 */
export const renderSubheader = ({ worksheet, currentRow, title, columnCount }: RenderSubheaderArgs): number => {
	const cell = worksheet.getCell(`A${currentRow}`);
	cell.value = title;
	cell.font = { bold: true, size: 12, color: { argb: "FF000000" } };
	cell.fill = SUBHEADER_FILL;
	cell.alignment = { horizontal: "left", indent: 1 };
	mergeAcrossRow(worksheet, currentRow, columnCount);
	return currentRow + 1;
};
